import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BookingDateTimePicker = ({ 
  providerId, 
  serviceId,
  service, // Service object with price, price_unit, session_duration, currency
  onDateTimeSelect,
  onBookingDetailsChange, // Callback for duration/price changes
  selectedDate,
  selectedTime 
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [bookedSlots, setBookedSlots] = useState([]);
  const [providerAvailability, setProviderAvailability] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);
  const [availableTimes, setAvailableTimes] = useState([]);
  const [blockedSlotInfo, setBlockedSlotInfo] = useState({}); // Maps time -> booking info
  
  // New state for adaptive booking based on price_unit
  const [selectedHours, setSelectedHours] = useState(1); // For "per hour" pricing
  const [selectedEndDate, setSelectedEndDate] = useState(null); // For "per project" pricing
  const [selectedEndTime, setSelectedEndTime] = useState(null); // For "per project" pricing
  
  // Multi-day booking state for "per day" pricing
  const [selectedDays, setSelectedDays] = useState(1); // Number of consecutive days
  const [endDay, setEndDay] = useState(null); // End day for multi-day booking
  
  // Get price unit from service (default to 'hour').
  // Services created via the form store values like "per hour"/"per day"/"per project",
  // while older/seeded data may use "hour"/"day". Normalize to the short form
  // ("per hour" -> "hour") so the hours/days selectors render consistently.
  const priceUnit = (service?.price_unit || 'hour').toString().toLowerCase().trim().replace(/^per\s+/, '');
  const sessionDuration = service?.session_duration || 1; // Hours per session
  const basePrice = service?.price || 0;
  const currency = service?.currency || 'PHP';
  
  // Calculate duration based on price unit
  const getBookingDuration = useCallback(() => {
    switch (priceUnit) {
      case 'hour':
        return selectedHours; // User selects hours
      case 'day':
        return `${selectedDays} day${selectedDays > 1 ? 's' : ''}`; // Multi-day support
      case 'session':
        return sessionDuration; // Provider-defined session length
      case 'project':
        return null; // Variable, provider sets end date
      default:
        return 1;
    }
  }, [priceUnit, selectedHours, sessionDuration, selectedDays]);
  
  // Calculate total price based on price unit
  const calculateTotalPrice = useCallback(() => {
    switch (priceUnit) {
      case 'hour':
        return basePrice * selectedHours;
      case 'day':
        return basePrice * selectedDays; // Multi-day: price × days
      case 'session':
      case 'project':
        return basePrice; // Fixed price
      default:
        return basePrice;
    }
  }, [priceUnit, basePrice, selectedHours, selectedDays]);
  
  // Notify parent of booking details changes
  useEffect(() => {
    if (onBookingDetailsChange) {
      onBookingDetailsChange({
        duration: getBookingDuration(),
        totalPrice: calculateTotalPrice(),
        priceUnit,
        selectedHours: priceUnit === 'hour' ? selectedHours : null,
        selectedDays: priceUnit === 'day' ? selectedDays : null,
        endDay: priceUnit === 'day' ? endDay : null,
      });
    }
  }, [selectedHours, selectedDays, endDay, priceUnit, onBookingDetailsChange, getBookingDuration, calculateTotalPrice]);

  // Default time slots (will be overridden by provider's working hours)
  const defaultTimeSlots = [
    '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
    '12:00', '12:30', '13:00', '13:30', '14:00', '14:30',
    '15:00', '15:30', '16:00', '16:30', '17:00', '17:30'
  ];

  useEffect(() => {
    fetchProviderAvailability();
    fetchProviderBookings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerId, currentMonth]);

  useEffect(() => {
    if (selectedDay) {
      calculateAvailableTimes(selectedDay);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDay, bookedSlots, providerAvailability]);

  const fetchProviderAvailability = async () => {
    try {
      const response = await axios.get(`${API}/provider/${providerId}/availability`);
      setProviderAvailability(response.data);
    } catch (error) {
      console.error('Error fetching provider availability:', error);
      setProviderAvailability(null);
    }
  };

  const fetchProviderBookings = async () => {
    setLoading(true);
    try {
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth() + 1;
      const response = await axios.get(`${API}/provider/${providerId}/bookings?year=${year}&month=${month}`);
      setBookedSlots(response.data.bookings || []);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      setBookedSlots([]);
    } finally {
      setLoading(false);
    }
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    return {
      daysInMonth: lastDay.getDate(),
      startingDay: firstDay.getDay()
    };
  };

  const isDateInPast = (day) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const checkDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    return checkDate < today;
  };

  const isToday = (day) => {
    const today = new Date();
    const checkDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    return checkDate.toDateString() === today.toDateString();
  };

  const getBookingsForDate = (day) => {
    const dateStr = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return bookedSlots.filter(b => {
      if (!['accepted', 'in_progress', 'pending', 'completed', 'completion_requested'].includes(b.status)) return false;
      const bookDate = b.date || b.scheduled_date;
      if (!bookDate) return false;
      // Exact date match
      if (bookDate === dateStr || (b.scheduled_date && b.scheduled_date.startsWith(dateStr))) return true;
      // Multi-day booking: check if dateStr falls within booking range
      if (b.end_date && bookDate <= dateStr && b.end_date >= dateStr) return true;
      if (b.requested_days && b.requested_days > 1) {
        try {
          const startDt = new Date(bookDate + 'T00:00:00');
          const endDt = new Date(startDt);
          endDt.setDate(endDt.getDate() + b.requested_days - 1);
          const checkDt = new Date(dateStr + 'T00:00:00');
          if (checkDt >= startDt && checkDt <= endDt) return true;
        } catch(e) {}
      }
      return false;
    });
  };

  // Check if a date is fully booked by a day-rate booking
  const isDateFullyBookedByDayRate = (day) => {
    const dateStr = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    if (providerAvailability?.fully_booked_dates?.includes(dateStr)) return true;
    // Also check from bookedSlots for multi-day bookings
    const dayBookings = getBookingsForDate(day);
    return dayBookings.some(b => b.price_unit === 'day' || b.price_unit === 'per day');
  };

  // Get working hours for a specific day
  const getWorkingHoursForDay = (day) => {
    if (!providerAvailability?.working_hours) return null;
    
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dayName = dayNames[date.getDay()];
    
    return providerAvailability.working_hours.find(wh => wh.day === dayName);
  };

  // Check if date is blocked by provider
  const isDateBlocked = (day) => {
    if (!providerAvailability?.blocked_dates) return false;
    
    const dateStr = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return providerAvailability.blocked_dates.includes(dateStr);
  };

  // Check if date falls within a blocked date range (from projects)
  const isDateInBlockedRange = (day) => {
    if (!providerAvailability?.blocked_date_ranges) return null;
    
    const dateStr = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    
    for (const range of providerAvailability.blocked_date_ranges) {
      if (dateStr >= range.start_date && dateStr <= range.end_date) {
        return range; // Return the blocking range for tooltip
      }
    }
    return null;
  };

  // Check if provider accepts bookings
  const acceptsBookings = providerAvailability?.accept_bookings !== false;

  const getDateStatus = (day) => {
    if (isDateInPast(day)) return 'past';
    if (isDateBlocked(day)) return 'blocked';
    if (isDateInBlockedRange(day)) return 'blocked-project';  // New: blocked by project
    
    // Check if fully booked by a day-rate booking (entire day blocked)
    if (isDateFullyBookedByDayRate(day)) return 'fully-booked';
    
    const workingHours = getWorkingHoursForDay(day);
    if (workingHours && !workingHours.is_working) return 'closed';
    
    const dayBookings = getBookingsForDate(day);
    if (dayBookings.length === 0) return 'available';
    
    // Get time slots for this day based on working hours
    const dayTimeSlots = getTimeSlotsForDay(day);
    
    // Helper function to convert time string to minutes since midnight
    const timeToMinutes = (timeStr) => {
      if (!timeStr) return null;
      const [hours, minutes] = timeStr.split(':').map(Number);
      return hours * 60 + minutes;
    };
    
    // Helper function to parse duration string
    const parseDuration = (durationStr) => {
      if (!durationStr) return 60;
      const lowerStr = durationStr.toLowerCase();
      const hoursMatch = lowerStr.match(/(\d+\.?\d*)\s*(?:hours?|hrs?|h)/);
      if (hoursMatch) return Math.round(parseFloat(hoursMatch[1]) * 60);
      const minsMatch = lowerStr.match(/(\d+)\s*(?:minutes?|mins?|m)/);
      if (minsMatch) return parseInt(minsMatch[1]);
      return 60;
    };
    
    // Build blocked ranges
    const blockedRanges = dayBookings.map(booking => {
      const startTime = timeToMinutes(booking.scheduled_time);
      if (startTime === null) return null;
      const durationMins = parseDuration(booking.estimated_duration);
      return { start: startTime, end: startTime + durationMins };
    }).filter(Boolean);
    
    // Count available slots
    let availableCount = 0;
    for (const time of dayTimeSlots) {
      const slotStart = timeToMinutes(time);
      const slotEnd = slotStart + 30;
      let isBlocked = false;
      for (const range of blockedRanges) {
        if (slotStart < range.end && slotEnd > range.start) {
          isBlocked = true;
          break;
        }
      }
      if (!isBlocked) availableCount++;
    }
    
    if (availableCount === 0) return 'fully-booked';
    if (availableCount < dayTimeSlots.length) return 'partially-booked';
    
    return 'available';
  };

  // Generate time slots based on provider's working hours
  const getTimeSlotsForDay = (day) => {
    const workingHours = getWorkingHoursForDay(day);
    if (!workingHours || !workingHours.is_working) return [];
    
    const slots = [];
    const [startHour, startMin] = workingHours.start_time.split(':').map(Number);
    const [endHour, endMin] = workingHours.end_time.split(':').map(Number);
    
    let currentHour = startHour;
    let currentMin = startMin;
    
    while (currentHour < endHour || (currentHour === endHour && currentMin < endMin)) {
      slots.push(`${String(currentHour).padStart(2, '0')}:${String(currentMin).padStart(2, '0')}`);
      currentMin += 30;
      if (currentMin >= 60) {
        currentMin = 0;
        currentHour += 1;
      }
    }
    
    return slots.length > 0 ? slots : defaultTimeSlots;
  };

  const calculateAvailableTimes = (day) => {
    if (isDateInPast(day) || isDateBlocked(day) || isDateInBlockedRange(day)) {
      setAvailableTimes([]);
      setBlockedSlotInfo({});
      return;
    }

    // If date is fully booked by a day-rate booking, block all times
    if (isDateFullyBookedByDayRate(day)) {
      setAvailableTimes([]);
      setBlockedSlotInfo({});
      return;
    }

    const workingHours = getWorkingHoursForDay(day);
    if (workingHours && !workingHours.is_working) {
      setAvailableTimes([]);
      setBlockedSlotInfo({});
      return;
    }

    const dayBookings = getBookingsForDate(day);
    
    // Get time slots for this day
    let available = getTimeSlotsForDay(day);
    
    // For today, also filter out past times
    if (isToday(day)) {
      const now = new Date();
      const currentHour = now.getHours();
      const currentMinute = now.getMinutes();
      available = available.filter(time => {
        const [hours, minutes] = time.split(':').map(Number);
        return hours > currentHour || (hours === currentHour && minutes > currentMinute);
      });
    }
    
    // Helper function to convert time string to minutes since midnight
    const timeToMinutes = (timeStr) => {
      if (!timeStr) return null;
      const [hours, minutes] = timeStr.split(':').map(Number);
      return hours * 60 + minutes;
    };
    
    // Helper function to parse duration string (e.g., "2 hours", "1.5 hours", "90 minutes")
    const parseDuration = (durationStr) => {
      if (!durationStr) return 60; // Default 1 hour
      
      const lowerStr = durationStr.toLowerCase();
      
      // Try to extract hours
      const hoursMatch = lowerStr.match(/(\d+\.?\d*)\s*(?:hours?|hrs?|h)/);
      if (hoursMatch) {
        return Math.round(parseFloat(hoursMatch[1]) * 60);
      }
      
      // Try to extract minutes
      const minsMatch = lowerStr.match(/(\d+)\s*(?:minutes?|mins?|m)/);
      if (minsMatch) {
        return parseInt(minsMatch[1]);
      }
      
      // If just a number, assume hours
      const numMatch = lowerStr.match(/^(\d+\.?\d*)$/);
      if (numMatch) {
        return Math.round(parseFloat(numMatch[1]) * 60);
      }
      
      return 60; // Default 1 hour
    };
    
    // Build list of blocked time ranges from bookings with booking info
    const blockedRanges = dayBookings.map(booking => {
      const startTime = timeToMinutes(booking.scheduled_time);
      if (startTime === null) return null;
      
      let durationMins = 60; // Default 1 hour
      
      // If booking has estimated_duration, use it
      if (booking.estimated_duration) {
        durationMins = parseDuration(booking.estimated_duration);
      }
      
      const endTime = startTime + durationMins;
      
      return { 
        start: startTime, 
        end: endTime, 
        booking: booking // Include booking info for tooltip
      };
    }).filter(Boolean);
    
    // Build map of blocked times to booking info for tooltips
    const blockedInfo = {};
    
    // Filter out time slots that overlap with any blocked range
    available = available.filter(time => {
      const slotStart = timeToMinutes(time);
      const slotEnd = slotStart + 30; // Each slot is 30 minutes
      
      // Check if this slot overlaps with any booked range
      for (const range of blockedRanges) {
        // Overlap if: slot starts before booking ends AND slot ends after booking starts
        if (slotStart < range.end && slotEnd > range.start) {
          // Store the booking info for this blocked slot
          blockedInfo[time] = range.booking;
          return false; // Blocked
        }
      }
      
      return true; // Available
    });
    
    setBlockedSlotInfo(blockedInfo);
    setAvailableTimes(available);
  };

  const handleDayClick = (day) => {
    if (isDateInPast(day)) return;
    if (isDateBlocked(day)) return;
    
    const workingHours = getWorkingHoursForDay(day);
    if (workingHours && !workingHours.is_working) return;
    
    const status = getDateStatus(day);
    if (status === 'fully-booked') return;
    
    setSelectedDay(day);
    const dateStr = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    onDateTimeSelect(dateStr, null);
  };

  const handleTimeClick = (time) => {
    if (!selectedDay) return;
    const dateStr = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}-${String(selectedDay).padStart(2, '0')}`;
    onDateTimeSelect(dateStr, time);
  };

  const formatTime = (time) => {
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hour12 = h % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  // Helper function to get currency symbol
  const getCurrencySymbol = (curr) => {
    const symbols = { PHP: '₱', USD: '$', EUR: '€', GBP: '£' };
    return symbols[curr] || curr;
  };

  // Helper function to get price unit label
  const getPriceUnitLabel = (unit) => {
    const labels = {
      hour: 'per hour',
      day: 'per day',
      session: 'per session',
      project: 'per project'
    };
    return labels[unit] || unit;
  };

  // Helper function to convert time to minutes
  const getTimeInMinutes = (timeStr) => {
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
  };

  // Helper function to get end time in minutes
  const getEndTimeMinutes = (startTime, durationHours) => {
    const startMinutes = getTimeInMinutes(startTime);
    return startMinutes + (durationHours * 60);
  };

  // Format end time given start time and duration
  const formatEndTime = (startTime, durationHours) => {
    const startMinutes = getTimeInMinutes(startTime);
    const endMinutes = startMinutes + (durationHours * 60);
    const endHours = Math.floor(endMinutes / 60);
    const endMins = endMinutes % 60;
    const h = endHours % 24;
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hour12 = h % 12 || 12;
    return `${hour12}:${String(endMins).padStart(2, '0')} ${ampm}`;
  };

  // Check if a duration is available starting from a given time
  const checkDurationAvailability = (startTime, durationHours) => {
    if (!selectedDay) return false;
    
    const startMinutes = getTimeInMinutes(startTime);
    const endMinutes = startMinutes + (durationHours * 60);
    
    // Check working hours limit
    const workingHours = getWorkingHoursForDay(selectedDay);
    if (workingHours) {
      const workEndMinutes = getTimeInMinutes(workingHours.end_time);
      if (endMinutes > workEndMinutes) return false;
    }
    
    // Check for conflicts with booked slots
    const dayBookings = getBookingsForDate(selectedDay);
    for (const booking of dayBookings) {
      if (!booking.scheduled_time) continue;
      
      const bookingStart = getTimeInMinutes(booking.scheduled_time);
      let bookingDuration = 60; // Default 1 hour
      if (booking.estimated_duration) {
        const durationStr = booking.estimated_duration.toLowerCase();
        const hoursMatch = durationStr.match(/(\d+\.?\d*)\s*(?:hours?|hrs?|h)/);
        if (hoursMatch) bookingDuration = parseFloat(hoursMatch[1]) * 60;
      }
      const bookingEnd = bookingStart + bookingDuration;
      
      // Check for overlap
      if (startMinutes < bookingEnd && endMinutes > bookingStart) {
        return false;
      }
    }
    
    return true;
  };

  // Check if a specific day is available for full-day booking
  const isDayAvailableForFullDay = (day) => {
    const dateStatus = getDateStatus(day);
    return dateStatus !== 'fully-booked' && !isDateInPast(day);
  };

  // Check how many consecutive days are available from a starting day
  const getMaxConsecutiveDays = (startDay) => {
    let maxDays = 0;
    const daysInMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0).getDate();
    
    for (let day = startDay; day <= daysInMonth; day++) {
      if (isDayAvailableForFullDay(day)) {
        maxDays++;
      } else {
        break;
      }
    }
    
    // Also check next month if we're at end of current month
    if (startDay + maxDays > daysInMonth) {
      // For simplicity, limit to current month for now
    }
    
    return Math.min(maxDays, 14); // Max 14 consecutive days
  };

  // Format date range for multi-day booking
  const formatDateRange = (startDay, numDays) => {
    const startDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), startDay);
    const endDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), startDay + numDays - 1);
    
    const options = { month: 'short', day: 'numeric' };
    const startStr = startDate.toLocaleDateString('en-US', options);
    const endStr = endDate.toLocaleDateString('en-US', options);
    
    if (numDays === 1) {
      return startStr;
    }
    return `${startStr} - ${endStr}`;
  };

  // Get end date string for multi-day booking
  const getEndDateString = (startDay, numDays) => {
    const endDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), startDay + numDays - 1);
    return `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`;
  };

  // Update end day when days count changes
  useEffect(() => {
    if (priceUnit === 'day' && selectedDay) {
      setEndDay(selectedDay + selectedDays - 1);
    }
  }, [selectedDays, selectedDay, priceUnit]);

  const prevMonth = () => {
    const newMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1);
    const today = new Date();
    today.setDate(1);
    today.setHours(0, 0, 0, 0);
    
    // Don't allow going to past months
    if (newMonth >= today || newMonth.getMonth() === today.getMonth()) {
      setCurrentMonth(newMonth);
      setSelectedDay(null);
    }
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
    setSelectedDay(null);
  };

  const { daysInMonth, startingDay } = getDaysInMonth(currentMonth);
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                      'July', 'August', 'September', 'October', 'November', 'December'];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const getDateClasses = (day) => {
    const status = getDateStatus(day);
    const isSelected = selectedDay === day;
    
    let classes = 'h-10 w-10 rounded-full flex items-center justify-center text-sm font-medium transition-all ';
    
    if (status === 'past') {
      classes += 'text-gray-300 cursor-not-allowed bg-gray-50';
    } else if (status === 'blocked') {
      classes += 'text-gray-400 cursor-not-allowed bg-gray-100 line-through';
    } else if (status === 'blocked-project') {
      classes += 'text-purple-400 cursor-not-allowed bg-purple-100 line-through';
    } else if (status === 'closed') {
      classes += 'text-gray-400 cursor-not-allowed bg-gray-100';
    } else if (status === 'fully-booked') {
      classes += 'text-red-400 cursor-not-allowed bg-red-50 line-through';
    } else if (status === 'partially-booked') {
      classes += 'text-yellow-700 bg-yellow-100 hover:bg-yellow-200 cursor-pointer';
    } else {
      classes += 'text-green-700 bg-green-100 hover:bg-green-200 cursor-pointer';
    }
    
    if (isSelected) {
      classes += ' ring-2 ring-blue-500 ring-offset-2';
    }
    
    if (isToday(day) && status !== 'past') {
      classes += ' font-bold';
    }
    
    return classes;
  };

  // Show message if provider not accepting bookings
  if (!acceptsBookings) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center" data-testid="not-accepting-bookings">
        <p className="text-yellow-800 font-medium">This provider is not accepting new bookings at this time.</p>
        <p className="text-sm text-yellow-600 mt-2">Please check back later or contact them directly.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg" data-testid="booking-date-time-picker">
      {/* Calendar Header */}
      <div className="flex items-center justify-between mb-4">
        <button
          type="button"
          onClick={prevMonth}
          className="p-2 hover:bg-gray-100 rounded-full transition disabled:opacity-50"
          disabled={currentMonth.getMonth() === new Date().getMonth() && currentMonth.getFullYear() === new Date().getFullYear()}
          data-testid="prev-month-btn"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <h3 className="text-lg font-bold">
          {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </h3>
        <button
          type="button"
          onClick={nextMonth}
          className="p-2 hover:bg-gray-100 rounded-full transition"
          data-testid="next-month-btn"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 mb-4 text-xs">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 bg-green-100 rounded-full border border-green-300"></div>
          <span>Available</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 bg-yellow-100 rounded-full border border-yellow-300"></div>
          <span>Some slots taken</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 bg-red-50 rounded-full border border-red-200"></div>
          <span>Fully booked</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 bg-gray-100 rounded-full border border-gray-200"></div>
          <span>Past date</span>
        </div>
      </div>

      {loading ? (
        <div className="h-48 flex items-center justify-center text-gray-500">
          <div className="flex items-center gap-2">
            <div className="animate-spin h-5 w-5 border-2 border-blue-600 border-t-transparent rounded-full"></div>
            Loading availability...
          </div>
        </div>
      ) : (
        <>
          {/* Day Headers */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {dayNames.map(day => (
              <div key={day} className="text-center text-xs font-medium text-gray-500 py-2">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Days */}
          <div className="grid grid-cols-7 gap-1 mb-4">
            {/* Empty cells */}
            {Array.from({ length: startingDay }).map((_, i) => (
              <div key={`empty-${i}`} className="h-10"></div>
            ))}
            
            {/* Days */}
            {Array.from({ length: daysInMonth }).map((_, i) => {
              const day = i + 1;
              // Check if this day is in the multi-day selection range
              const isInMultiDayRange = priceUnit === 'day' && selectedDay && selectedDays > 1 && 
                day >= selectedDay && day < selectedDay + selectedDays;
              const isMultiDayStart = priceUnit === 'day' && day === selectedDay && selectedDays > 1;
              const isMultiDayEnd = priceUnit === 'day' && day === selectedDay + selectedDays - 1 && selectedDays > 1;
              
              return (
                <div key={day} className="flex justify-center">
                  <button
                    type="button"
                    onClick={() => handleDayClick(day)}
                    disabled={isDateInPast(day) || getDateStatus(day) === 'fully-booked' || getDateStatus(day) === 'blocked' || getDateStatus(day) === 'blocked-project'}
                    className={`${getDateClasses(day)} ${
                      isInMultiDayRange && day !== selectedDay 
                        ? '!bg-blue-100 !text-blue-700 !border-blue-300' 
                        : ''
                    } ${isMultiDayStart ? 'rounded-r-none' : ''} ${isMultiDayEnd ? 'rounded-l-none' : ''} ${
                      isInMultiDayRange && !isMultiDayStart && !isMultiDayEnd ? 'rounded-none' : ''
                    }`}
                    data-testid={`calendar-day-${day}`}
                    title={getDateStatus(day) === 'blocked-project' ? isDateInBlockedRange(day)?.reason : ''}
                  >
                    {day}
                  </button>
                </div>
              );
            })}
          </div>

          {/* Time Slots - Adaptive based on price unit */}
          {selectedDay && (
            <div className="border-t pt-4" data-testid="time-slots-section">
              {/* Price Unit Info Banner */}
              <div className="mb-4 p-3 bg-gray-50 rounded-lg border">
                <div className="flex items-center gap-2 text-sm">
                  <span className="font-semibold text-gray-700">Pricing:</span>
                  <span className="text-blue-600 font-medium">
                    {getCurrencySymbol(currency)}{basePrice.toLocaleString()} {getPriceUnitLabel(priceUnit)}
                  </span>
                </div>
                {priceUnit === 'session' && (
                  <p className="text-xs text-gray-500 mt-1">
                    Each session is {sessionDuration} hour{sessionDuration !== 1 ? 's' : ''} long
                  </p>
                )}
                {priceUnit === 'day' && (
                  <p className="text-xs text-gray-500 mt-1">
                    Full day service (8 working hours with 1 hour break)
                  </p>
                )}
              </div>
              
              <h4 className="font-semibold mb-3">
                Select {priceUnit === 'project' ? 'Start ' : ''}Time for {monthNames[currentMonth.getMonth()]} {selectedDay}
              </h4>
              
              {availableTimes.length === 0 ? (
                <p className="text-gray-500 text-sm py-4 text-center">
                  No available time slots for this day
                </p>
              ) : (
                <>
                  {/* Per Hour - Duration selector */}
                  {priceUnit === 'hour' && selectedTime && (
                    <div className="mb-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                      <label className="block text-sm font-semibold text-orange-800 mb-2">
                        How many hours do you need?
                      </label>
                      <div className="flex items-center gap-3">
                        <select
                          value={selectedHours}
                          onChange={(e) => setSelectedHours(parseInt(e.target.value))}
                          className="px-4 py-2 border border-orange-300 rounded-lg focus:ring-2 focus:ring-orange-500 bg-white"
                        >
                          {Array.from({ length: 12 }, (_, i) => i + 1).map(h => {
                            // Check if this duration is available (no conflicts)
                            const endSlotMinutes = getEndTimeMinutes(selectedTime, h);
                            const isAvailable = checkDurationAvailability(selectedTime, h);
                            return (
                              <option key={h} value={h} disabled={!isAvailable}>
                                {h} hour{h > 1 ? 's' : ''} {!isAvailable ? '(unavailable)' : ''}
                              </option>
                            );
                          })}
                        </select>
                        <div className="text-sm">
                          <span className="text-gray-600">Total: </span>
                          <span className="font-bold text-orange-600">
                            {getCurrencySymbol(currency)}{(basePrice * selectedHours).toLocaleString()}
                          </span>
                        </div>
                      </div>
                      <p className="text-xs text-orange-600 mt-2">
                        {formatTime(selectedTime)} - {formatEndTime(selectedTime, selectedHours)}
                      </p>
                    </div>
                  )}
                  
                  {/* Per Day - Multi-day booking support */}
                  {priceUnit === 'day' && selectedTime && (
                    <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-sm font-semibold text-blue-800 mb-2">Full Day Booking</p>
                      
                      {/* Multi-day selector */}
                      <div className="mb-3">
                        <label className="block text-xs text-blue-700 mb-1 font-medium">
                          How many consecutive days?
                        </label>
                        <div className="flex items-center gap-3">
                          <select
                            value={selectedDays}
                            onChange={(e) => setSelectedDays(parseInt(e.target.value))}
                            className="px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white text-sm"
                          >
                            {Array.from({ length: getMaxConsecutiveDays(selectedDay) }, (_, i) => i + 1).map(d => (
                              <option key={d} value={d}>
                                {d} day{d > 1 ? 's' : ''}
                              </option>
                            ))}
                          </select>
                          <div className="text-sm">
                            <span className="text-blue-600">Total: </span>
                            <span className="font-bold text-blue-800">
                              {getCurrencySymbol(currency)}{(basePrice * selectedDays).toLocaleString()}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Date range display */}
                      <div className="bg-blue-100 rounded-lg p-2 text-xs">
                        <div className="flex items-center gap-2 text-blue-800">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          <span className="font-medium">{formatDateRange(selectedDay, selectedDays)}</span>
                        </div>
                        <p className="text-blue-600 mt-1 ml-6">
                          Daily start: {formatTime(selectedTime)} (8 working hrs + 1hr break each day)
                        </p>
                      </div>
                      
                      {/* Price breakdown for multi-day */}
                      {selectedDays > 1 && (
                        <div className="mt-3 pt-3 border-t border-blue-200 text-xs text-blue-700">
                          <div className="flex justify-between">
                            <span>Rate per day:</span>
                            <span>{getCurrencySymbol(currency)}{basePrice.toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Number of days:</span>
                            <span>×{selectedDays}</span>
                          </div>
                          <div className="flex justify-between font-bold text-blue-900 mt-1 pt-1 border-t border-blue-200">
                            <span>Total:</span>
                            <span>{getCurrencySymbol(currency)}{(basePrice * selectedDays).toLocaleString()}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Per Session - Show session duration */}
                  {priceUnit === 'session' && selectedTime && (
                    <div className="mb-4 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                      <p className="text-sm font-semibold text-purple-800 mb-1">Session Booking</p>
                      <p className="text-xs text-purple-600">
                        {formatTime(selectedTime)} - {formatEndTime(selectedTime, sessionDuration)} ({sessionDuration} hour{sessionDuration !== 1 ? 's' : ''})
                      </p>
                      <p className="text-sm font-bold text-purple-700 mt-2">
                        Total: {getCurrencySymbol(currency)}{basePrice.toLocaleString()}
                      </p>
                    </div>
                  )}
                  
                  {/* Per Project - Start time only */}
                  {priceUnit === 'project' && selectedTime && (
                    <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                      <p className="text-sm font-semibold text-green-800 mb-1">Project Start</p>
                      <p className="text-xs text-green-600">
                        You're selecting when you'd like the project to begin. The provider will set the timeline after accepting.
                      </p>
                      <p className="text-sm font-bold text-green-700 mt-2">
                        Project Price: {getCurrencySymbol(currency)}{basePrice.toLocaleString()}
                      </p>
                    </div>
                  )}
                
                  <div className="grid grid-cols-3 gap-2">
                    {getTimeSlotsForDay(selectedDay).map((time) => {
                      const isAvailable = availableTimes.includes(time);
                      const isSelected = selectedTime === time;
                      const blockingBooking = blockedSlotInfo[time];
                      
                      // Check if this is a past time (for today)
                      const isPastTime = isToday(selectedDay) && (() => {
                        const now = new Date();
                        const [hours, minutes] = time.split(':').map(Number);
                        return hours < now.getHours() || (hours === now.getHours() && minutes <= now.getMinutes());
                      })();
                      
                      // For per day pricing, just check basic availability (not duration)
                      // For per session, check if there's enough time for the session
                      // For per hour/project, show all available slots
                      const hasEnoughTime = priceUnit === 'session'
                        ? checkDurationAvailability(time, sessionDuration)
                        : true; // Per day, per hour, and per project don't need duration check for slot selection
                      
                      const effectivelyAvailable = isAvailable && hasEnoughTime;
                      
                      // Determine the tooltip text
                      let tooltipText = '';
                      if (isPastTime) {
                        tooltipText = 'Past time';
                      } else if (blockingBooking) {
                        const bookingTitle = blockingBooking.service_title || 'Booking';
                        const bookingTime = blockingBooking.scheduled_time ? formatTime(blockingBooking.scheduled_time) : '';
                        const duration = blockingBooking.estimated_duration || '1 hour';
                        tooltipText = `Booked: ${bookingTitle}${bookingTime ? ` at ${bookingTime}` : ''} (${duration})`;
                      } else if (!hasEnoughTime && isAvailable && priceUnit === 'session') {
                        tooltipText = `Not enough time for ${sessionDuration}hr session`;
                      }
                      
                      return (
                        <div key={time} className="relative group">
                          <button
                            type="button"
                            onClick={() => effectivelyAvailable && handleTimeClick(time)}
                            disabled={!effectivelyAvailable}
                            className={`w-full py-2 px-3 rounded-lg text-sm font-medium transition ${
                              !effectivelyAvailable
                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed line-through'
                                : isSelected
                                  ? 'bg-blue-600 text-white'
                                  : 'bg-green-50 text-green-700 hover:bg-green-100 border border-green-200'
                            }`}
                            data-testid={`time-slot-${time.replace(':', '')}`}
                          >
                            {formatTime(time)}
                          </button>
                          {/* Tooltip for blocked slots */}
                          {!effectivelyAvailable && tooltipText && (
                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50 shadow-lg">
                              {tooltipText}
                              <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Selected Date/Time Summary */}
          {selectedDate && (
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg" data-testid="selection-summary">
              <p className="text-sm text-blue-800">
                <strong>Selected:</strong> {selectedDate}
                {selectedTime && <span> at {formatTime(selectedTime)}</span>}
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default BookingDateTimePicker;
